import { FileText, X } from "lucide-react";

import type { AssetFileRef } from "./upload-assets/types";

import { THUMB_FRAME_CLASS } from "./upload-assets/thumb-frame";

function extOf(name: string): string | undefined {
  return name.match(/\.([\dA-Za-z]+)$/)?.[1]?.toUpperCase();
}

export function DocPlaceholder({ name }: { name: string }) {
  const ext = extOf(name);

  return (
    <div className="flex size-full flex-col items-center justify-center gap-sm text-text-quaternary">
      <FileText className="size-7" />
      {ext ? (
        <span className="rounded-[5px] border border-border-secondary bg-bg-secondary px-sm text-[11px] font-semibold text-text-secondary">
          {ext}
        </span>
      ) : null}
    </div>
  );
}

/**
 * A single uploaded-asset tile. Renders from the brief's durable `url` — no
 * metadata lookup. Anything that isn't an image (a PSD, a PDF) falls back to
 * the document placeholder with its extension.
 */
export function AssetThumb({
  file,
  onPreview,
  onRemove,
}: {
  file: AssetFileRef;
  onPreview?: (attachmentId: number) => void;
  onRemove?: (attachmentId: number) => void;
}) {
  const isImage = (file.mime ?? "").startsWith("image/");

  const content = isImage ? (
    <img alt={file.name} className="size-full object-cover" src={file.url} />
  ) : (
    <DocPlaceholder name={file.name} />
  );

  return (
    <div className={`group ${THUMB_FRAME_CLASS}`} title={file.name}>
      {onPreview ? (
        <button
          aria-label={`Preview ${file.name}`}
          className="size-full cursor-pointer outline-hidden focus-visible:ring-2 focus-visible:ring-fg-brand-primary focus-visible:ring-inset"
          onClick={() => onPreview(file.attachmentId)}
          type="button"
        >
          {content}
        </button>
      ) : (
        content
      )}
      {onRemove ? (
        <button
          aria-label={`Remove ${file.name}`}
          className="absolute top-[7px] right-[7px] grid size-6 cursor-pointer place-items-center rounded-full bg-bg-primary-solid/80 text-fg-white opacity-0 transition-opacity outline-hidden group-hover:opacity-100 focus-visible:opacity-100 focus-visible:ring-2 focus-visible:ring-fg-brand-primary [@media(hover:none)]:opacity-100"
          onClick={() => onRemove(file.attachmentId)}
          type="button"
        >
          <X className="size-3.5" />
        </button>
      ) : null}
    </div>
  );
}
